import { cn } from '@workspace/ui/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

const CONFIRM_TIMEOUT_MS = 5000

interface ConfirmButtonProps extends Omit<React.ComponentProps<'button'>, 'onClick'> {
  onConfirm: () => void
  confirmLabel?: string
}

function ConfirmButton({
  onConfirm,
  confirmLabel = 'Confirm',
  children,
  className,
  ...props
}: ConfirmButtonProps) {
  const [confirming, setConfirming] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  const reset = useCallback(() => {
    setConfirming(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleClick = () => {
    if (confirming) {
      reset()
      onConfirm()
    } else {
      setConfirming(true)
      timeoutRef.current = setTimeout(reset, CONFIRM_TIMEOUT_MS)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border text-sm font-medium uppercase outline-none transition-all',
        'h-10 w-24 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0',
        confirming
          ? 'border-destructive bg-destructive text-white shadow-xs'
          : 'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20',
        className
      )}
      {...props}
    >
      {confirming ? confirmLabel : children}
    </button>
  )
}

export { ConfirmButton }
